// Global
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';

// Local
import CtaButton, { CtaButtonProps } from 'components/helpers/Atoms/Cta/CtaButton';
import { usePearCommerceIsEnabled } from 'lib/hooks/usePearCommerceIsEnabled';
import { GtmEvent } from 'lib/utils/gtm-utils';
import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';
import { useContentItem } from 'lib/hooks/useContentItem';

export type PearShopCTAProps = CtaButtonProps & {
  hideCondition?: boolean;
};

const PearShopCTA = forwardRef<HTMLButtonElement, PearShopCTAProps>((props, ref) => {
  const { hideCondition = false, id, ...ctaButtonProps } = props;

  const { isEnabled } = usePearCommerceIsEnabled();
  const contentItem = useContentItem();
  const contentId = contentItem?.ContentId ?? '';
  const contentTitle = contentItem?.Title ?? '';
  const contentType = contentItem?.ContentType ?? '';

  const buttonElRef = useRef<HTMLButtonElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const setButtonRef = useCallback(
    (el: HTMLButtonElement | null) => {
      buttonElRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLButtonElement | null>).current = el;
      }
    },
    [ref]
  );

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const setupFocusRestoration = useCallback(() => {
    observerRef.current?.disconnect();

    const buttonEl = buttonElRef.current;
    if (!buttonEl) return;

    const scrollY = window.scrollY;
    let wasOpen = false;

    const modal = document.getElementById('pear-modal-for-recipe');

    const observer = new MutationObserver(() => {
      const isOpen = modal?.classList.contains('pear-modal-for-recipe-open') ?? false;

      if (isOpen) {
        wasOpen = true;
        return;
      }

      if (wasOpen) {
        observer.disconnect();
        observerRef.current = null;
        window.scrollTo({ top: scrollY, behavior: 'instant' as ScrollBehavior });
        buttonEl.focus({ preventScroll: true });
      }
    });

    observerRef.current = observer;

    if (modal) {
      // Watch the modal element for class/style changes
      observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
    } else {
      // Modal not yet in DOM — watch body for it to appear
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }, []);

  // Don't render if hideCondition is true or if Pear Commerce is not enabled
  if (hideCondition || !isEnabled) {
    return null;
  }

  const gtmEvent: GtmEvent = {
    event: 'shop_recipe',
    content_id: contentId,
    content_title: contentTitle,
    content_type: contentType,
    record_type: contentType,
  };

  return (
    <CtaButton
      {...(ctaButtonProps as CtaButtonProps)}
      ref={setButtonRef}
      id={id || 'pear-recipe-target'}
      onClickCapture={() => {
        pushToDataLayer({ ...gtmEvent } as DataLayerEvent);
        setupFocusRestoration();
      }}
    />
  );
});

PearShopCTA.displayName = 'PearShopCTA';

export default PearShopCTA;
